// Ads Workspace — single source of truth for the product-scoped artifact
// contract. Every product-scoped artifact (`ads:*`, `ugc-*`,
// `marketing-studio-refs`, `video:caption-project`) carries
// `metadata.productId`; this module enforces that at write time and answers
// "does this artifact belong to this product?" for every UI filter.
//
// Backend mirror: `backend/apps/core/studio/product_scope.py`.
// Core mirror:    `core/src/domains/ads/productArtifactScope.ts`.
// Keep the three subject lists in lockstep.

export const PRODUCT_SCOPED_SUBJECTS = new Set([
  "ads:research",
  "ads:image",
  "ads:video",
  "ads:clone",
  "ads:clone-source",
  "ads:campaign",
  "marketing-studio-refs",
  "ugc-portrait",
  "ugc-element",
  "ugc-text-frame",
  "ugc-video-plan",
  "video:caption-project",
]);

export function isProductScopedSubject(subject: string): boolean {
  return PRODUCT_SCOPED_SUBJECTS.has(subject);
}

/**
 * Throws if the subject is product-scoped and productId is missing/empty.
 * Use this at every artifact write site.
 */
export function assertProductScope(
  productId: string | null | undefined,
  subject: string,
): asserts productId is string {
  if (!productId && PRODUCT_SCOPED_SUBJECTS.has(subject)) {
    throw new Error(
      `Artifact subject "${subject}" is product-scoped — metadata.productId is required.`,
    );
  }
}

/**
 * Returns metadata with productId stamped on. Throws via assertProductScope
 * if the subject is product-scoped and productId is missing. Pass-through for
 * non-product-scoped subjects (just returns metadata unchanged).
 */
export function withProductScope<T extends Record<string, unknown>>(
  metadata: T,
  productId: string | null | undefined,
  subject: string,
): T & { productId?: string } {
  if (PRODUCT_SCOPED_SUBJECTS.has(subject)) {
    assertProductScope(productId, subject);
    return { ...metadata, productId };
  }
  return metadata;
}

export interface MatcherOptions {
  /**
   * For legacy artifacts created before strict productId enforcement.
   * Falls back to case-insensitive exact-match on productName.
   * Default: false. Only set true in UI filters that need to display legacy data.
   */
  allowLegacyNameFallback?: boolean;
}

/**
 * Single source of truth for "does this artifact belong to this product?".
 * Strict by default — only matches if metadata.productId === product.id.
 */
export function artifactMatchesProduct(
  artifact: { metadata?: Record<string, unknown> | null },
  product: { id: string; title: string },
  opts: MatcherOptions = {},
): boolean {
  const meta = (artifact.metadata as Record<string, unknown> | null) ?? {};
  const pid = typeof meta.productId === "string" ? meta.productId : undefined;
  if (pid) return pid === product.id;
  if (!opts.allowLegacyNameFallback) return false;
  const pname = typeof meta.productName === "string" ? meta.productName : undefined;
  if (!pname) return false;
  return pname.trim().toLowerCase() === product.title.trim().toLowerCase();
}
